/**
 * Custom hook for handling redirect logic
 * Manages navigation and redirect state
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRedirectConfig } from '../utils/brokerGuardUtils';

/**
 * Custom hook for redirect logic
 * @param {Object} guardState - guard state from useBrokerConnectionGuard
 * @returns {Object} - redirect actions and state
 */
export const useRedirectLogic = (guardState) => {
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);

  // Handle redirects
  useEffect(() => {
    if (guardState.shouldRedirect && guardState.redirectPath && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      
      const redirectConfig = getRedirectConfig(window.location.pathname, guardState.redirectPath);
      
      console.log('🔍 BrokerGuard: Executing redirect', {
        from: window.location.pathname,
        to: redirectConfig.path,
        config: redirectConfig
      });

      // Store current path for post-connect redirect if needed
      if (redirectConfig.state?.postConnectRedirect) {
        sessionStorage.setItem('postConnectRedirect', redirectConfig.state.postConnectRedirect);
      }

      // Execute redirect
      navigate(redirectConfig.path, { 
        replace: redirectConfig.replace,
        state: redirectConfig.state 
      });
    }
  }, [guardState.shouldRedirect, guardState.redirectPath, navigate]);

  // Reset redirect state
  const resetRedirect = () => {
    hasRedirectedRef.current = false;
  };

  // Manual redirect function
  const executeRedirect = (path, options = {}) => {
    const defaultOptions = { replace: true };
    const finalOptions = { ...defaultOptions, ...options };
    
    console.log('🔍 BrokerGuard: Manual redirect', { path, options: finalOptions });
    navigate(path, finalOptions);
  };

  return {
    hasRedirected: hasRedirectedRef.current,
    resetRedirect,
    executeRedirect
  };
};
